import { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import {
  Popover,
  MenuList,
  MenuItem,
  Typography,
  makeStyles,
  alpha,
} from '@material-ui/core';
import UserAvatar from '../userAvatar';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import { getPersonFullName } from '../../../utility/uiUtils';
import { isUserProfileAccessibleCheck } from '../../../utility/permissions';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  userInfo: {
    cursor: ({ canSeeProfile }) => (canSeeProfile ? 'pointer' : 'default'),
    margin: spacing(0, 4),
    width: 'auto',
    maxWidth: 180,
  },
  userFullName: {
    marginBottom: spacing(1),
    transition: 'color .25s ease-in',
    '&:hover': {
      color: ({ canSeeProfile }) =>
        canSeeProfile ? primary.blue1 : primary.bluish1,
    },
  },
  popover: {
    boxShadow: 'none',
    backgroundColor: 'transparent',
    marginTop: spacing(3),
    paddingRight: spacing(1.5),
    overflowX: 'visible',
    overflowY: 'visible',
  },
  menu: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish6}`,
    borderRadius: 4,
    filter: `drop-shadow(0px 4px 10px ${alpha(primary.black, 0.2)})`,
    width: 'fit-content',
    minWidth: 185,
    padding: 0,
  },
  menuItem: {
    padding: spacing(4),
    transition: 'background-color .25s ease-in',
    '&:hover': {
      backgroundColor: primary.bluish8,
    },
  },
  link: {
    textDecoration: 'none',
  },
  disabled: {
    cursor: 'default',
    '&:hover': {
      backgroundColor: primary.white,
    },
    '& span': {
      color: primary.bluish7,
    },
  },
  paper: {
    width: '400px',
    height: '135px',
    padding: spacing(5, 7, 6, 7),
    border: `1px solid ${primary.bluish7}`,
    borderRadius: 16,
    boxShadow: 'none',
    marginTop: spacing(2),
  },
}));

const UserMenu = ({
  className = null,
  translations,
  accessControl,
  user,
  menuItems,
  onGoToProfile,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const canSeeProfile = isUserProfileAccessibleCheck(
    accessControl,
    user.id,
    user.id
  );

  const classes = useStyles({ canSeeProfile });

  const handleGoToProfile = () => {
    if (canSeeProfile) {
      onGoToProfile(user.id);
    }
  };

  const handleOpenMenu = event => setAnchorEl(event.currentTarget);

  const handleCloseMenu = () => setAnchorEl(null);

  const handleClick = (e, item) => {
    if (!item.isDisabled && !item.isExternal) {
      item.action(e);
      handleCloseMenu();
    }
  };

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.userInfo} onClick={handleGoToProfile}>
        <TextBoxWithTooltip
          className={classes.userFullName}
          variant="subtitle1"
          text={getPersonFullName(user)}
        />
        <TextBoxWithTooltip
          variant="body2"
          text={user?.position?.name || translations.jobTitlePlaceholder}
        />
      </div>
      <UserAvatar
        user={user}
        onClickHandler={handleOpenMenu}
        clickableCaption
        medium
      />
      <Popover
        anchorEl={anchorEl}
        open={!!anchorEl}
        PaperProps={{
          classes: { root: classes.popover },
        }}
        onClose={handleCloseMenu}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
      >
        <MenuList classes={{ padding: classes.menu }}>
          {menuItems.map(item => {
            return (
              <MenuItem
                key={item.id}
                id={`user-menu-item-${item.id}`}
                classes={{
                  root: classNames(classes.menuItem, {
                    [classes.disabled]: item.isDisabled,
                  }),
                }}
                onClick={e => handleClick(e, item)}
                onBlur={handleCloseMenu}
              >
                {item.isExternal ? (
                  <Link
                    className={classes.link}
                    to={item.action}
                    target="_blank"
                  >
                    <Typography component="span">{item.name}</Typography>
                  </Link>
                ) : (
                  <Typography component="span">{item.name}</Typography>
                )}
              </MenuItem>
            );
          })}
        </MenuList>
      </Popover>
    </div>
  );
};

UserMenu.propTypes = {
  className: PropTypes.string,
  translations: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  menuItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onGoToProfile: PropTypes.func.isRequired,
};

export default UserMenu;
